import { createContext, useContext, useState } from 'react';
import {
  AppointmentSlot,
  AvailableAssociate,
  AvailableAssociateRes,
  TimePeriod,
} from '../types/appointments';

// datatype of the context declared here
interface IBookingContext {
  isLoading: boolean;
  isFetched: boolean;
  showSuggestionModal: boolean;
  showAvailableSlotModal: boolean;
  showUpdateClientModal: boolean;
  selectedDate: string;
  searchDate: string;
  serviceId: number;
  timePeriod: TimePeriod;
  partialTimeSlots: Array<AppointmentSlot>;
  availableAssociates: Array<AvailableAssociate>;
  selectedAssociate: AvailableAssociate | null;
  gridViewData: AvailableAssociateRes | null;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFetched: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSuggestionModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAvailableSlotModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUpdateClientModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;
  setSearchDate: React.Dispatch<React.SetStateAction<string>>;
  setServiceId: React.Dispatch<React.SetStateAction<number>>;
  setTimePeriod: React.Dispatch<React.SetStateAction<TimePeriod>>;
  setPartialTimeSlots: React.Dispatch<
    React.SetStateAction<Array<AppointmentSlot>>
  >;
  setAvailableAssociates: React.Dispatch<
    React.SetStateAction<Array<AvailableAssociate>>
  >;
  setSelectedAssociate: React.Dispatch<
    React.SetStateAction<AvailableAssociate | null>
  >;
  setGridViewData: React.Dispatch<
    React.SetStateAction<AvailableAssociateRes | null>
  >;
}

// creating the context here
const BookingContext = createContext<IBookingContext | null>(null);

const BookingProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuggestionModal, setShowSuggestionModal] =
    useState<boolean>(false);
  const [showAvailableSlotModal, setShowAvailableSlotModal] =
    useState<boolean>(false);
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [showUpdateClientModal, setShowUpdateClientModal] =
    useState<boolean>(false);

  const [timePeriod, setTimePeriod] = useState<TimePeriod>({
    slot_start_time: '',
    slot_end_time: '',
  });

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [searchDate, setSearchDate] = useState<string>('');
  const [serviceId, setServiceId] = useState<number>(0);

  const [partialTimeSlots, setPartialTimeSlots] = useState<
    Array<AppointmentSlot>
  >([]);
  const [availableAssociates, setAvailableAssociates] = useState<
    Array<AvailableAssociate>
  >([]);
  const [selectedAssociate, setSelectedAssociate] =
    useState<AvailableAssociate | null>(null);
  const [gridViewData, setGridViewData] =
    useState<AvailableAssociateRes | null>(null);

  // rendering the context provider here
  return (
    <BookingContext.Provider
      value={{
        isLoading,
        isFetched,
        showSuggestionModal,
        showAvailableSlotModal,
        showUpdateClientModal,
        selectedDate,
        searchDate,
        serviceId,
        timePeriod,
        partialTimeSlots,
        availableAssociates,
        selectedAssociate,
        gridViewData,
        setIsLoading,
        setIsFetched,
        setShowSuggestionModal,
        setShowAvailableSlotModal,
        setShowUpdateClientModal,
        setSelectedDate,
        setSearchDate,
        setServiceId,
        setTimePeriod,
        setPartialTimeSlots,
        setAvailableAssociates,
        setSelectedAssociate,
        setGridViewData,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export default BookingProvider;

// custom hook to access the context declared here
export const useBookingContext = () => {
  const context = useContext(BookingContext);

  if (!context) {
    throw new Error('useBookingContext must be within BookingProvider!');
  }

  return context;
};
